<template>
  <div class="wechat-editor" :class="{ 'is-fullscreen': fullscreen, 'is-disabled': disabled }">
    <!-- 工具栏：三行均分铺满（每行左右两端对齐，不留空白） -->
    <div class="we-toolbar" :id="toolbarId">
      <!-- ===== 第一行：文字格式 ===== -->
      <div class="we-toolbar-row we-toolbar-row--format">
        <span class="ql-formats">
          <select class="ql-header">
            <option value="1">标题 1</option>
            <option value="2">标题 2</option>
            <option value="3">标题 3</option>
            <option value="4">标题 4</option>
            <option selected>正文</option>
          </select>
        </span>
        <span class="ql-formats">
          <select class="ql-size">
            <option value="12px">12px</option>
            <option value="14px">14px</option>
            <option value="" selected>默认字号</option>
            <option value="18px">18px</option>
            <option value="20px">20px</option>
            <option value="24px">24px</option>
            <option value="30px">30px</option>
          </select>
        </span>
        <span class="ql-formats">
          <button class="ql-bold" title="加粗 Ctrl+B"></button>
          <button class="ql-italic" title="斜体 Ctrl+I"></button>
          <button class="ql-underline" title="下划线 Ctrl+U"></button>
          <button class="ql-strike" title="删除线"></button>
          <button class="ql-code-inline" title="行内代码">
            <svg viewBox="0 0 18 18">
              <polyline class="ql-stroke" points="5 6 2 9 5 12"></polyline>
              <polyline class="ql-stroke" points="13 6 16 9 13 12"></polyline>
              <line class="ql-stroke" x1="10" y1="5" x2="8" y2="13"></line>
            </svg>
          </button>
        </span>
        <span class="ql-formats">
          <button class="ql-list" value="ordered" title="有序列表"></button>
          <button class="ql-list" value="bullet" title="无序列表"></button>
          <button class="ql-indent" value="-1" title="减少缩进"></button>
          <button class="ql-indent" value="+1" title="增加缩进"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-color" title="字体颜色"></select>
          <select class="ql-background" title="背景颜色"></select>
        </span>
      </div>

      <!-- ===== 第二行：对齐 / 行高 / 插入（左对齐，不撑满） ===== -->
      <div class="we-toolbar-row we-toolbar-row--align">
        <span class="ql-formats">
          <select class="ql-align" title="对齐方式"></select>
          <select class="ql-lineheight" title="行高">
            <option value="" selected>默认行高</option>
            <option value="1">1.0</option>
            <option value="1.5">1.5</option>
            <option value="1.75">1.75</option>
            <option value="2">2.0</option>
            <option value="2.5">2.5</option>
            <option value="3">3.0</option>
          </select>
        </span>
        <span class="ql-formats">
          <button class="ql-blockquote" title="引用"></button>
          <button class="ql-link" title="超链接"></button>
          <button class="ql-image" title="插入图片 (可多选)"></button>
          <button class="ql-video" title="插入视频" @click.prevent="openVideoDialog"></button>
          <button class="ql-table" title="插入表格" @click="openTableDialog">
            <svg viewBox="0 0 18 18">
              <rect class="ql-stroke" x="2.5" y="3.5" width="13" height="11" rx="1"></rect>
              <line class="ql-stroke" x1="2.5" y1="7.2" x2="15.5" y2="7.2"></line>
              <line class="ql-stroke" x1="2.5" y1="10.8" x2="15.5" y2="10.8"></line>
              <line class="ql-stroke" x1="7" y1="3.5" x2="7" y2="14.5"></line>
              <line class="ql-stroke" x1="11" y1="3.5" x2="11" y2="14.5"></line>
            </svg>
          </button>
          <button class="ql-hr" title="分割线">
            <svg viewBox="0 0 18 18">
              <line class="ql-stroke" x1="2" y1="9" x2="16" y2="9"></line>
            </svg>
          </button>
          <button class="ql-clean" title="清除格式"></button>
        </span>
      </div>

      <!-- ===== 第三行：重置 + 文字类按钮 =====
           说明：重置 / 撤销 / 重做 / 源码 / 全屏 / 清空 全部是「文字类按钮」，统一放最后一行。
           按钮宽度在 CSS 里写死（min-width + flex:0 0 auto），任何情况下都不会被挤到重叠。 -->
      <div class="we-toolbar-row we-toolbar-row--tools">
        <span class="we-tools-right">
          <button type="button" class="we-btn-text" title="重置为初始内容（撤销全部修改）" @click="resetAll">
            <i class="el-icon-refresh"></i>
            <span>重置</span>
          </button>
          <button type="button" class="we-btn-text" title="撤销 Ctrl+Z" @click="undo">
            <i class="el-icon-refresh-left"></i>
            <span>撤销</span>
          </button>
          <button type="button" class="we-btn-text" title="重做 Ctrl+Y" @click="redo">
            <i class="el-icon-refresh-right"></i>
            <span>重做</span>
          </button>
          <button type="button" class="we-btn-text" :class="{ 'is-active': showSource }" :title="showSource ? '返回可视化编辑' : '查看/编辑 HTML 源码'" @click="toggleSource">
            <i :class="showSource ? 'el-icon-edit-outline' : 'el-icon-view'"></i>
            <span>{{ showSource ? '返回编辑' : '源码' }}</span>
          </button>
          <button type="button" class="we-btn-text" :class="{ 'is-active': fullscreen }" :title="fullscreen ? '退出全屏' : '全屏编辑'" @click="toggleFullscreen">
            <i :class="fullscreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
            <span>{{ fullscreen ? '退出' : '全屏' }}</span>
          </button>
          <button type="button" class="we-btn-text we-btn-clear" title="清空全部内容" @click="clearAll">
            <i class="el-icon-delete"></i>
            <span>清空</span>
          </button>
        </span>
      </div>
    </div>

    <!-- 编辑区 -->
    <div ref="body" class="we-body">
      <quill-editor
        v-show="!showSource"
        ref="quill"
        v-model="content"
        :options="editorOptions"
        class="we-quill"
        @ready="onReady"
        @change="onChange"
        @blur="onBlur"
      />
      <!-- 图片/视频选中浮层：上移/下移工具条 + 右下角缩放手柄 -->
      <div v-show="selVisible" class="we-sel-toolbar" :style="selToolbarStyle">
        <button type="button" class="we-sel-btn" title="上移一格" @click="moveSelected(-1)">
          <i class="el-icon-top"></i>
        </button>
        <button type="button" class="we-sel-btn" title="下移一格" @click="moveSelected(1)">
          <i class="el-icon-bottom"></i>
        </button>
      </div>
      <div
        v-show="selVisible"
        class="we-sel-handle"
        :style="selHandleStyle"
        title="拖拽调整大小"
        @mousedown.prevent="startResize"
      ></div>
      <!-- 图片拖拽换位时的插入指示线 -->
      <div v-show="dragVisible" class="we-drag-line" :style="dragStyle"></div>
      <textarea
        v-show="showSource"
        v-model="content"
        class="we-source"
        spellcheck="false"
        placeholder="在此编辑 HTML 源码…"
      ></textarea>
    </div>

    <!-- 底部状态栏 -->
    <div class="we-footer">
      <span v-if="showSource" class="we-footer-tip">
        <i class="el-icon-warning-outline"></i> 源码模式：直接编辑 HTML，字数统计不可用
      </span>
      <span v-else class="we-footer-tip">
        <i class="el-icon-document"></i> 详情页正文
      </span>
      <span class="we-footer-count">
        <b>{{ plainLength }}</b> 字
      </span>
    </div>

    <!-- 全屏时的确认栏 -->
    <div v-if="fullscreen" class="we-fullscreen-bar">
      <span class="we-fullscreen-hint">全屏编辑中，按 Esc 退出</span>
      <el-button size="small" @click="toggleFullscreen">退出全屏</el-button>
    </div>

    <!-- 表格插入弹窗 -->
    <el-dialog
      title="插入表格"
      :visible.sync="tableDialog"
      width="320px"
      append-to-body
      custom-class="we-table-dialog"
    >
      <div class="we-table-picker">
        <div class="we-table-picker-label">
          已选 <b>{{ tableRows }}</b> 行 × <b>{{ tableCols }}</b> 列
        </div>
        <div class="we-table-grid" @mouseleave="hoverRow = 0; hoverCol = 0">
          <div
            v-for="r in 8"
            :key="'r' + r"
            class="we-table-row"
          >
            <span
              v-for="c in 8"
              :key="'c' + c"
              class="we-table-cell"
              :class="{ active: r <= hoverRow && c <= hoverCol }"
              @mouseenter="hoverRow = r; hoverCol = c"
              @click="confirmTable(r, c)"
            ></span>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" @click="tableDialog = false">取消</el-button>
      </div>
    </el-dialog>

    <!-- 视频插入弹窗 -->
    <el-dialog
      title="插入视频"
      :visible.sync="videoDialog"
      width="480px"
      append-to-body
      custom-class="we-video-dialog"
    >
      <el-tabs v-model="videoTab">
        <el-tab-pane label="视频链接" name="url">
          <div class="we-video-form">
            <el-input
              v-model="videoUrl"
              placeholder="支持腾讯视频 / 优酷 / B站 iframe 地址，或 .mp4 直链"
              clearable
            />
            <p class="we-video-hint">
              腾讯视频请粘贴通用代码中的 iframe src 地址，例如
              <code>https://v.qq.com/txp/iframe/player.html?vid=xxxx</code>
            </p>
          </div>
        </el-tab-pane>
        <el-tab-pane label="本地上传" name="upload">
          <div class="we-video-form">
            <el-upload
              drag
              action="#"
              :show-file-list="false"
              :http-request="handleVideoUpload"
              accept="video/*"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">拖拽视频到此处，或<em>点击上传</em></div>
            </el-upload>
          </div>
        </el-tab-pane>
      </el-tabs>
      <div slot="footer">
        <el-button size="small" @click="videoDialog = false">取消</el-button>
        <el-button size="small" type="primary" :disabled="videoTab === 'url' && !videoUrl" @click="confirmVideo">
          确定插入
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Quill from 'quill'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { quillEditor } from 'vue-quill-editor'
import uploadToQiniu from '@/util/qiniu-upload'

// 自定义字号：Quill 1.x 默认只认 small/large/huge，这里改为白名单 px 值
try {
  const Size = Quill.import('attributors/style/size')
  Size.whitelist = ['12px', '14px', '16px', '18px', '20px', '24px', '30px']
  Quill.register(Size, true)
} catch (e) {
  console.warn('[WechatEditor] 自定义字号注册失败，使用默认字号', e)
}

// 自定义行高（Quill 无内置，用 style 型 attributor）
try {
  const Parchment = Quill.import('parchment')
  const LineHeight = new Parchment.Attributor.Style('lineheight', 'line-height', {
    scope: Parchment.Scope.BLOCK,
    whitelist: ['1', '1.5', '1.75', '2', '2.5', '3']
  })
  Quill.register(LineHeight, true)
} catch (e) {
  console.warn('[WechatEditor] 行高注册失败，行高选项将不生效', e)
}

// 分割线 blot（Quill 无内置 hr）
try {
  const BlockEmbed = Quill.import('blots/block/embed')
  class HrBlot extends BlockEmbed {
    static create() {
      const node = super.create()
      node.setAttribute('contenteditable', 'false')
      return node
    }
  }
  HrBlot.blotName = 'hr'
  HrBlot.tagName = 'hr'
  Quill.register(HrBlot)
} catch (e) {
  console.warn('[WechatEditor] 分割线注册失败', e)
}

// 行内代码 blot（公众号常用）
try {
  const Inline = Quill.import('blots/inline')
  class CodeInlineBlot extends Inline {
    static create(value) {
      const node = super.create(value)
      node.setAttribute('spellcheck', 'false')
      return node
    }
  }
  CodeInlineBlot.blotName = 'code-inline'
  CodeInlineBlot.tagName = 'code'
  CodeInlineBlot.className = 'ql-code-inline'
  Quill.register(CodeInlineBlot)
} catch (e) {
  console.warn('[WechatEditor] 行内代码注册失败', e)
}

let uid = 0

export default {
  name: 'WechatEditor',
  components: { quillEditor },  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: '请输入内容…' },
    minHeight: { type: Number, default: 320 },
    disabled: { type: Boolean, default: false },
    /** 上传图片：返回 Promise<url>；不传则默认七牛直传 */
    uploadImage: { type: Function, default: null },
    /** 七牛存储 key 前缀（图片） */
    imageDir: { type: String, default: 'editor/image' },
    /** 上传视频：返回 Promise<url>；不传则默认七牛直传 */
    uploadVideo: { type: Function, default: null },
    /** 七牛存储 key 前缀（视频） */
    videoDir: { type: String, default: 'editor/video' }
  },
  data() {
    const id = 'we-toolbar-' + (++uid)
    return {
      toolbarId: id,
      content: this.value || '',
      /** 进入编辑器时的初始内容快照，「重置」按钮据此恢复 */
      initialContent: this.value || '',
      fullscreen: false,
      showSource: false,
      tableDialog: false,
      hoverRow: 0,
      hoverCol: 0,
      tableRows: 3,
      tableCols: 3,
      videoDialog: false,
      videoTab: 'url',
      videoUrl: '',
      // 图片/视频选中浮层与拖拽指示线状态
      selVisible: false,
      selToolbarStyle: {},
      selHandleStyle: {},
      dragVisible: false,
      dragStyle: {},
      editorOptions: {
        theme: 'snow',
        placeholder: this.placeholder,
        modules: {
          toolbar: '#' + id,
          clipboard: { matchVisual: false }
        }
      }
    }
  },
  computed: {
    plainLength() {
      return String(this.content || '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length
    }
  },
  watch: {
    value(val) {
      if (val !== this.content) this.content = val || ''
    },
    // 关键：把 content 单向同步给父组件的 v-model。
    // 内层 quill-editor 的 v-model 只更新本组件的 content，父组件的 form.intro
    // 只有收到 input 事件才会变——不补这条，提交时 intro 恒为空串。
    content(val) {
      if (val !== this.value) this.$emit('input', val)
    }
  },
  mounted() {
    window.addEventListener('paste', this.handlePaste, true)
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.handlePaste, true)
    document.removeEventListener('keydown', this.handleKeydown)
    document.body.style.overflow = ''
    // 清理下拉面板修复用的事件监听
    if (this._pickerCleanup) this._pickerCleanup()
    // 清理图片/视频选中与拖拽的事件监听
    this.unbindMediaInteract()
    if (this._onViewportChange) {
      window.removeEventListener('scroll', this._onViewportChange, true)
      window.removeEventListener('resize', this._onViewportChange)
    }
  },
  methods: {
    onReady(quill) {
      this.quill = quill
      const toolbar = quill.getModule('toolbar')
      // 图片：接管默认行为（默认转 base64 且不可控）
      toolbar.addHandler('image', this.pickImage)
      // 分割线
      toolbar.addHandler('hr', () => {
        const range = quill.getSelection(true)
        quill.insertEmbed(range.index, 'hr', true, Quill.sources.USER)
        quill.setSelection(range.index + 1, Quill.sources.SILENT)
      })
      this.bindMediaInteract()
      this.bindPickerPanelFix()
    },

    /**
     * 修复下拉面板被祖先 overflow 裁切的问题。
     * 背景：弹框 .el-dialog__body 是 overflow-y:auto 的滚动容器，会把
     * quill picker 的绝对定位面板（.ql-picker-options）裁掉一截（表现为"面板被截取"）。
     * 做法：面板展开时改为 position:fixed，坐标按 label 的位置算，脱离所有 overflow 上下文；
     *      并在滚动/resize 时同步收起，避免面板"漂"在原地。
     */
    bindPickerPanelFix() {
      const root = this.$el
      if (!root) return
      const place = picker => {
        const label = picker.querySelector('.ql-picker-label')
        const options = picker.querySelector('.ql-picker-options')
        if (!label || !options) return
        const r = label.getBoundingClientRect()
        options.style.position = 'fixed'
        options.style.left = r.left + 'px'
        options.style.top = r.bottom + 4 + 'px'
        options.style.minWidth = Math.max(r.width, 76) + 'px'
        options.style.zIndex = '4000'   // 高于 el-dialog(2000+)
      }
      const clear = picker => {
        const options = picker.querySelector('.ql-picker-options')
        if (!options) return
        options.style.position = ''
        options.style.left = ''
        options.style.top = ''
        options.style.minWidth = ''
        options.style.zIndex = ''
      }
      // 用事件委托监听展开状态（quill 展开时给 picker 加 .ql-expanded）
      const onToggle = e => {
        const picker = e.target.closest && e.target.closest('.ql-picker')
        if (!picker) return
        // 用 rAF 等 quill 更新完 class
        requestAnimationFrame(() => {
          if (picker.classList.contains('ql-expanded')) place(picker)
          else clear(picker)
        })
      }
      root.addEventListener('click', onToggle, true)
      this._pickerCleanup = () => {
        root.removeEventListener('click', onToggle, true)
        root.querySelectorAll('.ql-picker').forEach(clear)
      }
      // 面板 fixed 后不再跟随容器滚动，滚动/缩放时直接收起
      this._onViewportChange = () => {
        root.querySelectorAll('.ql-picker.ql-expanded').forEach(p => {
          p.classList.remove('ql-expanded')
          clear(p)
        })
      }
      window.addEventListener('scroll', this._onViewportChange, true)
      window.addEventListener('resize', this._onViewportChange)
    },

    onChange() {
      // v-model 已负责 input 事件，这里只额外抛出 change 供父组件监听
      this.$emit('change', this.content)
      // 内容变化后（undo/清空/源码回填）选中的元素可能已被替换，浮层随之收起
      if (this.selTarget && this.quill && !this.quill.root.contains(this.selTarget)) {
        this.hideSel()
      }
    },
    onBlur() {
      this.$emit('blur', this.content)
    },

    /* ---------- 图片 ---------- */
    pickImage() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.multiple = true
      input.onchange = () => {
        const files = Array.prototype.slice.call(input.files || [])
        if (!files.length) return
        files.forEach(f => this.insertImage(f))
      }
      input.click()
    },
    insertImage(file) {
      const quill = this.quill
      if (!quill) return
      const range = quill.getSelection(true) || { index: quill.getLength() }
      const insert = url => {
        quill.insertEmbed(range.index, 'image', url, Quill.sources.USER)
        quill.setSelection(range.index + 1, Quill.sources.SILENT)
      }
      if (typeof this.uploadImage === 'function') {
        this.uploadImage(file).then(insert).catch(() => {
          this.$message && this.$message.error('图片上传失败')
        })
        return
      }
      // 默认链路：七牛直传，插入外链 URL
      uploadToQiniu(file, this.imageDir).then(insert).catch(() => {
        this.$message && this.$message.error('图片上传失败，请重试')
      })
    },

    /* ---------- 图片/视频：选中、缩放、移动 ---------- */
    isMedia(el) {
      return !!el && (
        el.tagName === 'IMG' ||
        (el.tagName === 'IFRAME' && el.classList.contains('ql-video'))
      )
    },
    // 事件委托：点击选中图片/视频，点空白处收起；编辑器滚动时收起（浮层定位失效）
    bindMediaInteract() {
      const root = this.quill && this.quill.root
      if (!root) return
      this._onMediaClick = e => {
        const t = e.target
        if (this.isMedia(t)) {
          this.selectMedia(t)
          return
        }
        // 视频 iframe 已设 pointer-events:none，点击落在其所在段落上，按坐标命中
        if (t && t.querySelector) {
          const v = t.querySelector('iframe.ql-video')
          if (v) {
            const vr = v.getBoundingClientRect()
            if (e.clientX >= vr.left && e.clientX <= vr.right && e.clientY >= vr.top && e.clientY <= vr.bottom) {
              this.selectMedia(v)
              return
            }
          }
        }
        this.hideSel()
      }
      this._onEditorScroll = () => this.hideSel()
      this._onMediaDragStart = e => this.onMediaDragStart(e)
      this._onMediaDragOver = e => this.onMediaDragOver(e)
      this._onMediaDrop = e => this.onMediaDrop(e)
      this._onMediaDragEnd = () => this.onMediaDragEnd()
      root.addEventListener('click', this._onMediaClick)
      root.addEventListener('scroll', this._onEditorScroll)
      root.addEventListener('dragstart', this._onMediaDragStart)
      root.addEventListener('dragover', this._onMediaDragOver)
      root.addEventListener('drop', this._onMediaDrop)
      root.addEventListener('dragend', this._onMediaDragEnd)
    },
    unbindMediaInteract() {
      const root = this.quill && this.quill.root
      if (!root) return
      root.removeEventListener('click', this._onMediaClick)
      root.removeEventListener('scroll', this._onEditorScroll)
      root.removeEventListener('dragstart', this._onMediaDragStart)
      root.removeEventListener('dragover', this._onMediaDragOver)
      root.removeEventListener('drop', this._onMediaDrop)
      root.removeEventListener('dragend', this._onMediaDragEnd)
    },
    selectMedia(el) {
      this.selTarget = el
      this.selVisible = true
      this.repositionSel()
    },
    hideSel() {
      this.selVisible = false
      this.selTarget = null
    },
    // 浮层定位：相对 .we-body（编辑区自身滚动时由 scroll 监听收起兜底）
    repositionSel() {
      const el = this.selTarget
      const body = this.$refs.body
      if (!el || !body || !el.parentNode) return
      const er = el.getBoundingClientRect()
      const br = body.getBoundingClientRect()
      const left = er.left - br.left
      const top = er.top - br.top
      this.selHandleStyle = {
        left: (left + er.width - 8) + 'px',
        top: (top + er.height - 8) + 'px'
      }
      this.selToolbarStyle = {
        left: left + 'px',
        top: (top - 32) + 'px'
      }
    },
    // 右下角手柄拖拽改宽度（视频按 16:9 同步改高）
    startResize(e) {
      const el = this.selTarget
      const body = this.$refs.body
      if (!el || !body) return
      const isVideo = el.tagName === 'IFRAME'
      if (isVideo) el.style.minHeight = '0'
      const maxW = body.clientWidth - 4
      const startW = el.getBoundingClientRect().width
      const sx = e.clientX
      const onMove = ev => {
        const w = Math.max(60, Math.min(maxW, Math.round(startW + ev.clientX - sx)))
        el.style.width = w + 'px'
        if (isVideo) {
          el.style.height = Math.round(w * 9 / 16) + 'px'
        } else {
          // img 的 width 属性 quill 会随 formats 保留，源码模式往返不丢
          el.setAttribute('width', w)
        }
        this.repositionSel()
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    // 上移/下移一格：图片=挪到相邻段落末尾/开头；视频(独立行)=与相邻行互换
    moveSelected(dir) {
      const quill = this.quill
      const el = this.selTarget
      const blot = el && Quill.find(el)
      if (!quill || !blot) return
      const isVideo = el.tagName === 'IFRAME'
      const src = el.getAttribute('src')
      if (!src) return
      const index = quill.getIndex(blot)
      const lines = quill.getLines()
      // 图片是行内 embed，要找到它所在的顶层块；视频本身就是一行
      let host = blot
      while (host && host.parent && host.parent.domNode !== quill.root) {
        host = host.parent
      }
      const li = lines.indexOf(host)
      const adj = li + (dir === -1 ? -1 : 1)
      if (li < 0 || !lines[adj]) return
      const adjIndex = quill.getIndex(lines[adj])
      quill.deleteText(index, 1, Quill.sources.USER)
      let insertIndex
      if (isVideo) {
        // 删除后相邻行若在原位置之后起点前移一位；插到其末尾（dir=1）/开头（dir=-1）
        insertIndex = dir === -1 ? adjIndex : adjIndex - 1 + lines[adj].length()
      } else {
        insertIndex = dir === -1
          ? adjIndex + lines[adj].length() - 1  // 上一段结尾（\n 之前）
          : adjIndex - 1                        // 下一段开头（删除后起点前移一位）
      }
      insertIndex = Math.max(0, Math.min(quill.getLength(), insertIndex))
      const type = isVideo ? 'video' : 'image'
      quill.insertEmbed(insertIndex, type, src, Quill.sources.USER)
      // 重新选中挪过去的那个元素
      this.$nextTick(() => {
        const leaf = quill.getLeaf(insertIndex)
        const node = leaf && leaf[0] && leaf[0].domNode
        if (node && this.isMedia(node)) this.selectMedia(node)
        else this.hideSel()
      })
    },
    // 屏幕坐标 → quill 文档 index（拖拽落点定位用）
    caretIndexAt(x, y) {
      const quill = this.quill
      if (!quill || !document.caretRangeFromPoint) return null
      const range = document.caretRangeFromPoint(x, y)
      if (!range || !quill.root.contains(range.startContainer)) return null
      const node = range.startContainer
      const offset = range.startOffset
      const blot = Quill.find(node.nodeType === 3 ? node.parentNode : node)
      if (!blot) return null
      let index = quill.getIndex(blot)
      if (node.nodeType === 3) {
        index += offset
      } else {
        for (let i = 0; i < offset; i++) {
          const childBlot = node.childNodes[i] && Quill.find(node.childNodes[i])
          if (childBlot && childBlot.length) index += childBlot.length()
        }
      }
      return Math.max(0, Math.min(quill.getLength(), index))
    },
    /* 图片拖拽换位 */
    onMediaDragStart(e) {
      const t = e.target
      if (!this.isMedia(t) || t.tagName !== 'IMG') return
      const blot = Quill.find(t)
      if (!blot) return
      this._dragging = { index: this.quill.getIndex(blot), src: t.getAttribute('src') }
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        try { e.dataTransfer.setData('text/plain', ' ') } catch (err) {}
      }
    },
    onMediaDragOver(e) {
      if (!this._dragging) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
      const index = this.caretIndexAt(e.clientX, e.clientY)
      if (index == null || index === this._dragging.index) {
        this.dragVisible = false
        return
      }
      const bounds = this.quill.getBounds(index)
      const body = this.$refs.body
      if (!bounds || !body || !bounds.height) {
        this.dragVisible = false
        return
      }
      const br = body.getBoundingClientRect()
      this.dragStyle = {
        left: (bounds.left - br.left + body.scrollLeft) + 'px',
        top: (bounds.top - br.top + body.scrollTop) + 'px',
        height: Math.max(bounds.height, 20) + 'px'
      }
      this.dragVisible = true
      this._dropIndex = index
    },
    onMediaDrop(e) {
      const drag = this._dragging
      this.dragVisible = false
      this._dragging = null
      if (!drag) return
      e.preventDefault()
      const quill = this.quill
      let target = this._dropIndex
      this._dropIndex = null
      if (target == null) target = this.caretIndexAt(e.clientX, e.clientY)
      if (target == null || target === drag.index) return
      quill.deleteText(drag.index, 1, Quill.sources.USER)
      if (target > drag.index) target -= 1
      quill.insertEmbed(Math.max(0, Math.min(quill.getLength(), target)), 'image', drag.src, Quill.sources.USER)
      this.hideSel()
    },
    onMediaDragEnd() {
      this._dragging = null
      this._dropIndex = null
      this.dragVisible = false
    },

    /* ---------- 粘贴 ---------- */
    /** 粘贴：拦截剪贴板中的图片文件，保留外部富文本（公众号文章） */
    handlePaste(e) {
      if (this.disabled || this.showSource) return
      const items = (e.clipboardData && e.clipboardData.items) || []
      const imgs = []
      for (let i = 0; i < items.length; i++) {
        const it = items[i]
        if (it.kind === 'file' && it.type.indexOf('image') > -1) {
          const f = it.getAsFile()
          if (f) imgs.push(f)
        }
      }
      if (!imgs.length) {
        // 没有内嵌图片：交给 quill 默认粘贴（它会自动清掉外部冗余样式，保留基本结构）
        return
      }
      // 有图片：浏览器默认会把图片转成 base64 塞进 HTML，这里改走自己的上传流程
      e.preventDefault()
      const html = e.clipboardData.getData('text/html')
      const text = e.clipboardData.getData('text/plain')
      if (html) {
        this.quill.clipboard.dangerouslyPasteHTML(html, Quill.sources.USER)
      } else if (text) {
        this.quill.clipboard.dangerouslyPasteHTML(
          text.replace(/</g, '&lt;').replace(/\n/g, '<br/>'),
          Quill.sources.USER
        )
      }
      imgs.forEach(f => this.insertImage(f))
    },

    /* ---------- 表格 ---------- */
    openTableDialog() {
      this.tableDialog = true
      this.hoverRow = 0
      this.hoverCol = 0
    },
    confirmTable(rows, cols) {
      this.tableRows = rows
      this.tableCols = cols
      this.tableDialog = false
      this.insertTable(rows, cols)
    },
    insertTable(rows, cols) {
      const quill = this.quill
      if (!quill) return
      let html = '<table>'
      for (let r = 0; r < rows; r++) {
        html += '<tr>'
        for (let c = 0; c < cols; c++) {
          html += '<td><br></td>'
        }
        html += '</tr>'
      }
      html += '</table><p><br></p>'
      const range = quill.getSelection(true) || { index: quill.getLength() }
      quill.clipboard.dangerouslyPasteHTML(range.index, html, Quill.sources.USER)
      quill.setSelection(range.index + 1, Quill.sources.SILENT)
    },

    /* ---------- 行内代码 ---------- */
    // 说明：ql-code-inline 按钮由 quill 原生 toolbar 通过 blotName=code-inline 自动处理，
    // 此处保留方法仅供外部按需调用（如快捷键）。
    insertInlineCode() {
      const quill = this.quill
      if (!quill) return
      const range = quill.getSelection(true)
      if (!range) return
      const format = quill.getFormat(range)
      quill.format('code-inline', !format['code-inline'], Quill.sources.USER)
      quill.focus()
    },

    /* ---------- 撤销 / 重做 ---------- */
    undo() {
      if (this.quill) this.quill.history.undo()
    },
    redo() {
      if (this.quill) this.quill.history.redo()
    },
    /* ---------- 重置：恢复到进入编辑器时的初始内容 ---------- */
    resetAll() {
      const doReset = () => {
        const init = this.initialContent || ''
        this.content = init
        if (this.quill) {
          this.quill.setContents([], Quill.sources.SILENT)
          this.quill.clipboard.dangerouslyPasteHTML(init, Quill.sources.SILENT)
          this.quill.history.clear()   // 清掉历史，重置后不能再撤销回改动的状态
          this.quill.focus()
        }
        this.$emit('input', init)
        this.$emit('change', init)
        this.$message && this.$message.success('已重置为初始内容')
      }
      // 内容与初始一致时无需重置
      if ((this.content || '') === (this.initialContent || '')) {
        this.$message && this.$message.info('当前已是初始内容')
        return
      }
      this.$confirm('确定重置为初始内容吗？当前未保存的修改将丢失。', '重置确认', {
        type: 'warning',
        confirmButtonText: '确定重置',
        cancelButtonText: '取消'
      }).then(doReset).catch(() => {})
    },
    /* ---------- 清空 ---------- */
    clearAll() {
      const doClear = () => {
        if (this.quill) {
          this.quill.setText('', Quill.sources.USER)
          this.quill.focus()
        }
        this.content = ''
        this.$emit('input', '')
        this.$emit('change', '')
      }
      if (this.content && this.plainLength > 0) {
        this.$confirm('确定清空全部内容吗？此操作不可撤销。', '提示', {
          type: 'warning',
          confirmButtonText: '确定清空',
          cancelButtonText: '取消'
        }).then(doClear).catch(() => {})
      } else {
        doClear()
      }
    },

    /* ---------- 视频 ---------- */
    openVideoDialog() {
      this.videoUrl = ''
      this.videoTab = 'url'
      this.videoDialog = true
    },
    confirmVideo() {
      if (this.videoTab === 'url') {
        const url = String(this.videoUrl || '').trim()
        if (!url) return
        this.insertVideo(url)
        this.videoDialog = false
      }
    },
    handleVideoUpload(option) {
      // 默认七牛直传；父组件传了 upload-video 则用父组件的实现
      const task = typeof this.uploadVideo === 'function'
        ? this.uploadVideo(option.file)
        : uploadToQiniu(option.file, this.videoDir)
      task
        .then(url => {
          this.insertVideo(url)
          this.videoDialog = false
        })
        .catch(() => {
          this.$message && this.$message.error('视频上传失败')
        })
    },
    insertVideo(url) {
      const quill = this.quill
      if (!quill) return
      const range = quill.getSelection(true) || { index: quill.getLength() }
      quill.insertEmbed(range.index, 'video', url, Quill.sources.USER)
      quill.setSelection(range.index + 1, Quill.sources.SILENT)
    },

    /* ---------- 源码 ---------- */
    toggleSource() {
      this.showSource = !this.showSource
      if (!this.showSource) {
        // 从源码切回编辑：把 textarea 内容灌回 quill
        this.$nextTick(() => {
          if (this.quill) {
            this.quill.clipboard.dangerouslyPasteHTML(this.content || '', Quill.sources.SILENT)
          }
        })
      }
    },

    /* ---------- 全屏 ---------- */
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen
      document.body.style.overflow = this.fullscreen ? 'hidden' : ''
    },
    handleKeydown(e) {
      if (e.keyCode === 27 && this.fullscreen) {
        this.toggleFullscreen()
      }
    },

    /** 供父组件调用：取纯文本 */
    getText() {
      return this.content ? this.content.replace(/<[^>]+>/g, '') : ''
    }
  }
}
</script>

<style>
/* ================= 编辑器外框：公众号风格工具栏 ================= */
.wechat-editor {
  display: flex;
  flex-direction: column;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  /* 注意：不能 overflow:hidden —— 下拉面板会被裁。圆角靠各子块自身处理 */
  overflow: visible;
  background: #fff;
  transition: border-color 0.2s;
}
/* 工具栏与编辑区各自做圆角，避免外框无法用 overflow:hidden 时圆角穿帮 */
.wechat-editor > .we-toolbar.ql-toolbar.ql-snow {
  border-radius: 5px 5px 0 0;
}
.wechat-editor > .we-body {
  border-radius: 0 0 5px 5px;
}
.wechat-editor:focus-within {
  border-color: var(--primary, #8e1103);
}

/* ================= 工具栏：固定三行 ================= */
.we-toolbar.ql-toolbar.ql-snow {
  border: none;
  border-bottom: 1px solid #ebeef5;
  padding: 12px 18px;            /* 内边距：按钮组不贴边，四周留呼吸空间 */
  background: #fafafa;
  display: block;
  box-sizing: border-box;
  /* 不能用 overflow:hidden —— 会把下拉展开面板一起裁掉（曾出现"弹框被截取"）。
     改为 visible，让 .ql-picker-options 能正常伸出；横向溢出由外层 .wechat-editor 兜住。 */
  overflow: visible;
  position: relative;
  z-index: 5;                    /* 保证下拉面板盖在编辑区之上 */
}

/* 行：三行固定结构，行内 nowrap，行间 10px 留白 */
.we-toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;             /* 三行已按宽度分配好，行内不折行 */
  padding: 0;
  box-sizing: border-box;
  min-height: 40px;
  justify-content: flex-start;   /* 默认左对齐，组间距由 .ql-formats 的 margin 控制 */
}
.we-toolbar-row + .we-toolbar-row {
  margin-top: 10px;              /* 行间距加大 */
}

/* 第一行：5 组，内容饱满 —— 用 space-between 铺满两端 */
.we-toolbar-row--format {
  justify-content: space-between;
}

/* 第二行：只有 2 组，若用 space-between 会把两组推到两端、中间空一大块，
   显得不自然 —— 故保持左对齐，两组按自然间距紧挨着排。 */
.we-toolbar-row--align {
  justify-content: flex-start;
}

/* 第三行：重置 + 文字类按钮，整行右对齐 */
.we-toolbar-row--tools {
  justify-content: flex-end;
  min-height: 34px;
}

/* 组：自然宽度。行内靠「组间距」+ space-between(仅第一行) 排布 */
.we-toolbar.ql-toolbar.ql-snow .ql-formats {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 1 auto;              /* 不强制等分，保持自然宽度 */
  margin: 0;
  padding: 0;
  min-width: 0;
  float: none;
  vertical-align: top;
  position: relative;
}
/* 组间距 & 竖线说明：
   普通模式：第一行 5 组共 550px，容器 560px，只剩 10px 余量（4 个间隙各 2.5px），
     所以第一行必须靠 space-between 铺满，且**放不下组间竖线**（插不进去）；
     第二行只有 2 组，靠下方 margin-left 提供自然间距。
   全屏模式：行1/行2 改为 flex:0 0 auto 并肩后，space-between 失效，
     此时统一用下方 margin-left 提供组间距（见全屏段规则）。
   结论：三行一律不加组间竖线，用间距区分即可，视觉更干净。 */
.we-toolbar.ql-toolbar.ql-snow
  .we-toolbar-row--align
  .ql-formats
  + .ql-formats {
  margin-left: 24px;           /* 第二行两组之间的自然间距 */
}
/* 全屏：行1/行2 并肩且各自宽度收缩到内容，space-between 不再起作用，
   故给这两行的相邻组补上固定间距，避免组与组贴在一起。 */
.wechat-editor.is-fullscreen
  .we-toolbar-row--format
  .ql-formats
  + .ql-formats,
.wechat-editor.is-fullscreen
  .we-toolbar-row--align
  .ql-formats
  + .ql-formats {
  margin-left: 18px;
}
/* 清掉 quill 自带 clearfix 伪元素 */
.we-toolbar.ql-toolbar.ql-snow .ql-formats::after,
.we-toolbar.ql-toolbar.ql-snow::after {
  display: none;
  content: none;
}

/* 按钮：30px 见方，图标 19px */
.we-toolbar.ql-toolbar.ql-snow .ql-formats button {
  width: 30px;
  height: 30px;
  padding: 0;
  margin: 0 3px;
  border-radius: 5px;
  transition: background 0.15s;
  flex: 0 0 auto;
}
.we-toolbar.ql-toolbar.ql-snow .ql-formats button:hover {
  background: #f2e9e7;
}
.we-toolbar.ql-toolbar.ql-snow .ql-formats button svg {
  width: 19px;
  height: 19px;
  float: none;
  display: block;
  margin: 0 auto;
}

/* picker（下拉）*/
.we-toolbar.ql-toolbar.ql-snow .ql-formats .ql-picker {
  margin: 0 3px;
  flex: 0 0 auto;
  font-size: 13px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-header {
  width: 72px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-size {
  width: 78px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-align,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-color,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-background {
  width: 32px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-lineheight {
  width: 80px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label {
  padding: 0 7px;
  height: 30px;
  line-height: 30px;
  border-radius: 5px;
  border-color: transparent;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label::before {
  line-height: 30px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label:hover {
  background: #f2e9e7;
  border-color: transparent;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label svg {
  right: 5px;
  margin-top: -9px;
  width: 16px;
}
/* picker 展开面板
   默认 absolute 定位；展开时由 JS 临时改成 position:fixed 并计算坐标，
   以逃出 .el-dialog__body 的 overflow 裁切（见 bindPickerPanelFix）。 */
.we-toolbar.ql-toolbar.ql-snow .ql-picker-options {
  padding: 6px 8px;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  font-size: 13px;
  color: #303133;              /* 明确字色，避免继承成与背景同色 */
  background: #fff;
  border: 1px solid #e4e7ed;
  z-index: 20;                 /* absolute 兜底层级；fixed 时 JS 会设 4000 */
  max-height: 260px;           /* 选项多时可滚动，不无限拉长 */
  overflow-y: auto;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker-options .ql-picker-item {
  padding: 6px 10px;
  border-radius: 4px;
  line-height: 1.4;
  color: #303133;
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker-options .ql-picker-item:hover {
  background: #f2e9e7;
  color: var(--primary, #8e1103);
}

/* 对齐 picker 是 quill 内置 IconPicker：label 和选项里都是 svg 图标（无文字、无箭头）。
   通用 label svg 规则里的 margin-top:-9px / absolute 定位对图标 picker 是错的，单独扳回来；
   展开面板改为一行四个图标（flex 横排），替代默认竖排窄条。 */
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-align .ql-picker-label svg {
  position: static;
  float: left;
  margin: 7px 0 0 8px;
  width: 16px;
  height: 16px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-icon-picker .ql-picker-options {
  display: flex;
  flex-wrap: nowrap;
  padding: 5px;
  overflow-y: hidden;
}
.we-toolbar.ql-toolbar.ql-snow .ql-icon-picker .ql-picker-item {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  padding: 4px;
  margin: 2px;
  line-height: 1;
  border-radius: 4px;
}
.we-toolbar.ql-toolbar.ql-snow .ql-icon-picker .ql-picker-item svg {
  display: block;
  width: 18px;
  height: 18px;
  margin: 0 auto;
}

/* ===== 自定义 picker 的选项文字（关键） =====
   quill 只为内置值写死了 content（如 .ql-header[data-value="1"]::before { content:'Heading 1' }），
   自定义的 ql-lineheight、以及改成 px 的 ql-size，quill 不认识 → ::before 没有 content
   → 展开面板里全是空白（曾出现"下拉打开一片空"的问题）。
   这里统一用 attr(data-value) 兜底渲染文字；带 data-label 的优先用 data-label。 */
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-label::before,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-item::before,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-lineheight .ql-picker-label::before,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-lineheight .ql-picker-item::before {
  content: attr(data-value);
}
/* 有 data-label 时优先显示 label（如「默认字号」「默认行高」）——label 和下拉项都要，
   否则默认选中项 value 为空串时 label 的 attr(data-value) 渲染不出文字（quill 会把空 data-value remove 掉） */
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-label[data-label]:not([data-label=''])::before,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-lineheight .ql-picker-label[data-label]:not([data-label=''])::before,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-item[data-label]:not([data-label=''])::before,
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-label]:not([data-label=''])::before {
  content: attr(data-label);
}
/* 默认项（无 data-value）显示 label */
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-item:not([data-value])::before {
  content: '默认字号';
}
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-lineheight .ql-picker-item:not([data-value])::before {
  content: '默认行高';
}
/* 行高选项不要被 quill 的 header/size 字号规则影响，统一 13px */
.we-toolbar.ql-toolbar.ql-snow .ql-picker.ql-lineheight .ql-picker-item::before {
  font-size: 13px;
}

/* 工具栏 hover / active 主色（默认蓝 #06c → 项目朱红） */
.we-toolbar.ql-toolbar.ql-snow button:hover,
.we-toolbar.ql-toolbar.ql-snow button:focus,
.we-toolbar.ql-toolbar.ql-snow button.ql-active,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label:hover,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label.ql-active,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-item:hover,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-item.ql-selected {
  color: var(--primary, #8e1103);
}
.we-toolbar.ql-toolbar.ql-snow button:hover .ql-stroke,
.we-toolbar.ql-toolbar.ql-snow button:focus .ql-stroke,
.we-toolbar.ql-toolbar.ql-snow button.ql-active .ql-stroke,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-stroke,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-item:hover .ql-stroke,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-item.ql-selected .ql-stroke {
  stroke: var(--primary, #8e1103);
}
.we-toolbar.ql-toolbar.ql-snow button:hover .ql-fill,
.we-toolbar.ql-toolbar.ql-snow button:focus .ql-fill,
.we-toolbar.ql-toolbar.ql-snow button.ql-active .ql-fill,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label:hover .ql-fill,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-fill,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-item:hover .ql-fill,
.we-toolbar.ql-toolbar.ql-snow .ql-picker-item.ql-selected .ql-fill {
  fill: var(--primary, #8e1103);
}

/* 第三行按钮组容器：整行右对齐。
   按钮尺寸已写死，这里不wrap（三行结构已保证宽度够），
   用 margin-left 控制按钮间距（不用 gap，Element/旧内核下 <button> 的 gap 不稳）。 */
.we-tools-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;             /* 按钮尺寸固定，不会溢出，无需折行 */
  flex: 0 0 auto;
  margin-left: auto;             /* 推到最右 */
}

/* 文字按钮（重置/撤销/重做/源码/全屏/清空）
   —— 尺寸全部写死，禁止 flex 压缩，从根本上杜绝「被挤到重叠」。 */
.we-btn-text {
  /* 内部布局：图标 + 文字横向排布，显式声明防止重叠 */
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;

  /* 尺寸锁死：任何情况下都不可被压缩或拉伸 */
  flex: 0 0 auto;
  min-width: 68px;               /* 固定最小宽度，6 个按钮宽度一致、排列整齐 */
  height: 32px;                  /* 高度也固定 */
  box-sizing: border-box;

  padding: 0 12px;
  margin-left: 10px;             /* 按钮间距加大 */
  font-size: 13px;
  line-height: 1;
  font-family: inherit;
  color: #606266;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
  overflow: visible;
  transition: all 0.2s;
  /* 清掉 Element 按钮可能继承的样式 */
  text-align: center;
  vertical-align: middle;
}
.we-btn-text:first-child {
  margin-left: 0;
}
.we-btn-text:hover {
  color: var(--primary, #8e1103);
  border-color: var(--primary, #8e1103);
  background: #fff;
}
.we-btn-text > i {
  flex: 0 0 auto;
  margin-right: 5px;
  font-size: 14px;
  line-height: 1;
  display: inline-block;
}
/* 图标+文字之间用 margin，避免 flex gap 在小程序/旧内核下失效 */
.we-btn-text > span {
  flex: 0 0 auto;
  display: inline-block;
  line-height: 1;
  font-size: 13px;
  white-space: nowrap;
}
/* 源码 / 全屏 处于开启态 */
.we-btn-text.is-active {
  color: #fff;
  background: var(--primary, #8e1103);
  border-color: var(--primary, #8e1103);
}
.we-btn-text.is-active:hover {
  color: #fff;
  background: var(--primary-200, #b8391f);
  border-color: var(--primary-200, #b8391f);
}
/* 清空按钮：危险色文字 */
.we-btn-clear {
  color: #f56c6c;
}
.we-btn-clear:hover {
  color: #fff;
  background: #f56c6c;
  border-color: #f56c6c;
}

/* ================= 编辑区 ================= */
.we-body {
  flex: 1;
  min-height: 0;
  position: relative;
  background: #fff;
}
.we-quill.quill-editor .ql-container.ql-snow {
  border: none;
  font-size: 15px;
  height: auto;
}
.we-quill.quill-editor .ql-editor {
  min-height: 320px;
  padding: 18px 22px;
  line-height: 1.75;
  color: #303133;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}
.we-quill.quill-editor .ql-editor.ql-blank::before {
  color: #c0c4cc;
  font-style: normal;
  left: 22px;
}

/* 正文内元素样式 */
.we-quill.quill-editor .ql-editor h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 20px 0 12px;
  line-height: 1.4;
}
.we-quill.quill-editor .ql-editor h2 {
  font-size: 20px;
  font-weight: 600;
  margin: 18px 0 10px;
  line-height: 1.45;
}
.we-quill.quill-editor .ql-editor h3 {
  font-size: 17px;
  font-weight: 600;
  margin: 16px 0 8px;
  line-height: 1.5;
}
.we-quill.quill-editor .ql-editor h4 {
  font-size: 15px;
  font-weight: 600;
  margin: 14px 0 8px;
}
.we-quill.quill-editor .ql-editor p {
  margin: 10px 0;
}
.we-quill.quill-editor .ql-editor blockquote {
  border-left: 4px solid var(--primary, #8e1103);
  background: #fafafa;
  padding: 10px 14px;
  margin: 14px 0;
  color: #606266;
  border-radius: 0 4px 4px 0;
}
.we-quill.quill-editor .ql-editor img {
  max-width: 100%;
  border-radius: 4px;
  display: block;
  margin: 12px 0;
}
/* 对齐格式加在段落 class 上（text-align），对块级 img 无效——用 margin auto 实现居中/靠右 */
.we-quill.quill-editor .ql-editor .ql-align-center img,
.we-quill.quill-editor .ql-editor .ql-align-right img {
  margin-left: auto;
  margin-right: auto;
}
.we-quill.quill-editor .ql-editor .ql-align-center iframe.ql-video,
.we-quill.quill-editor .ql-editor .ql-align-right iframe.ql-video {
  margin-left: auto;
  margin-right: auto;
}
.we-quill.quill-editor .ql-editor hr {
  border: none;
  border-top: 1px solid #ebeef5;
  margin: 20px 0;
}
.we-quill.quill-editor .ql-editor a {
  color: var(--primary, #8e1103);
  text-decoration: underline;
}
.we-quill.quill-editor .ql-editor code,
.we-quill.quill-editor .ql-editor .ql-code-inline {
  background: #f2f3f5;
  color: #d6336c;
  padding: 1px 5px;
  border-radius: 3px;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 0.92em;
}
.we-quill.quill-editor .ql-editor pre {
  background: #f6f8fa;
  border-radius: 6px;
  padding: 12px 14px;
  overflow-x: auto;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 13px;
  margin: 14px 0;
}

/* 表格 */
.we-quill.quill-editor .ql-editor table {
  border-collapse: collapse;
  width: 100%;
  margin: 14px 0;
  table-layout: fixed;
}
.we-quill.quill-editor .ql-editor table td,
.we-quill.quill-editor .ql-editor table th {
  border: 1px solid #dcdfe6;
  padding: 8px 10px;
  min-width: 40px;
  line-height: 1.6;
}
.we-quill.quill-editor .ql-editor table td:focus {
  outline: 2px solid var(--primary-300, #f7e4e1);
  outline-offset: -2px;
}

/* 视频 */
.we-quill.quill-editor .ql-editor iframe.ql-video,
.we-quill.quill-editor .ql-editor .ql-video {
  width: 100%;
  min-height: 320px;
  margin: 12px 0;
  border-radius: 4px;
  /* 编辑器内不播放：iframe 会吞掉点击，无法选中做缩放/移动（预览交给小程序端） */
  pointer-events: none;
}

/* 图片：可拖拽换位 */
.we-quill.quill-editor .ql-editor img {
  cursor: grab;
}

/* 选中图片/视频的浮动工具条与缩放手柄（挂在 .we-body 上） */
.we-sel-toolbar {
  position: absolute;
  z-index: 30;
  display: flex;
  padding: 2px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}
.we-sel-btn {
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #303133;
  cursor: pointer;
  border-radius: 4px;
  padding: 0;
  font-size: 14px;
}
.we-sel-btn:hover {
  color: var(--primary, #8e1103);
  background: #f7ece9;
}
.we-sel-handle {
  position: absolute;
  z-index: 31;
  width: 14px;
  height: 14px;
  background: #fff;
  border: 2px solid var(--primary, #8e1103);
  border-radius: 50%;
  cursor: nwse-resize;
}
/* 图片拖拽换位的插入指示线 */
.we-drag-line {
  position: absolute;
  z-index: 29;
  width: 3px;
  border-radius: 2px;
  background: var(--primary, #8e1103);
  pointer-events: none;
}

/* 链接提示气泡：跟队主题色 */
.we-quill.quill-editor .ql-snow .ql-tooltip {
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}
.we-quill.quill-editor .ql-snow .ql-tooltip.ql-editing input[type='text'] {
  border-radius: 3px;
}

/* ================= 源码模式 ================= */
.we-source {
  display: block;
  width: 100%;
  min-height: 320px;
  padding: 18px 22px;
  border: none;
  outline: none;
  resize: vertical;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.7;
  color: #303133;
  background: #fcfcfc;
  box-sizing: border-box;
}

/* ================= 底部状态栏 ================= */
.we-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 14px;
  border-top: 1px solid #ebeef5;
  background: #fafafa;
  font-size: 12px;
  color: #909399;
}
.we-footer-tip i {
  margin-right: 3px;
}
.we-footer-count {
  flex: 0 0 auto;
}
.we-footer-count b {
  color: #606266;
  font-weight: 600;
}

/* ================= 全屏 ================= */
.wechat-editor.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  border-radius: 0;
  border: none;
}
.wechat-editor.is-fullscreen .we-body {
  overflow-y: auto;
}
.wechat-editor.is-fullscreen .we-quill .ql-editor,
.wechat-editor.is-fullscreen .we-source {
  min-height: calc(100vh - 150px);
}

/* 全屏时屏幕宽裕，工具栏压成两行：
   第一行 = 所有图标类工具（原行1文字格式 + 原行2对齐插入，合并并肩）
   第二行 = 文字类按钮（重置/撤销/重做/源码/全屏/清空）
   实现：工具栏改 flex 容器 + 换行；行1、行2 并肩占第一行，行3 强制 100% 独占第二行。 */
.wechat-editor.is-fullscreen .we-toolbar.ql-toolbar.ql-snow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0;
  row-gap: 10px;
  padding: 10px 20px;
}
.wechat-editor.is-fullscreen .we-toolbar-row {
  min-height: 36px;
  margin-top: 0;              /* 行间距统一交给容器的 row-gap */
}
/* 行1（文字格式）与行2（对齐/插入）并肩，共占第一行 */
.wechat-editor.is-fullscreen .we-toolbar-row--format {
  flex: 0 0 auto;
  justify-content: flex-start;
}
.wechat-editor.is-fullscreen .we-toolbar-row--align {
  flex: 1 1 auto;             /* 吃掉第一行剩余空间 */
  justify-content: flex-start;
  min-width: 0;
  padding-left: 20px;         /* 与行1 之间留出间距 */
}
/* 行3（文字按钮）强制换行，独占第二行，按钮靠右 */
.wechat-editor.is-fullscreen .we-toolbar-row--tools {
  flex: 0 0 100%;
  width: 100%;
  justify-content: flex-end;
  min-height: 36px;
}
.we-fullscreen-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}
.we-fullscreen-hint {
  font-size: 12px;
  color: #909399;
}

/* ================= 禁用 ================= */
.wechat-editor.is-disabled {
  opacity: 0.7;
  pointer-events: none;
}

/* ================= 表格插入弹窗 ================= */
.we-table-picker-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 10px;
  text-align: center;
}
.we-table-picker-label b {
  color: var(--primary, #8e1103);
}
.we-table-grid {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: fit-content;
  margin: 0 auto;
}
.we-table-row {
  display: flex;
  gap: 3px;
}
.we-table-cell {
  width: 24px;
  height: 24px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.12s;
}
.we-table-cell.active {
  background: var(--primary-300, #f7e4e1);
  border-color: var(--primary, #8e1103);
}

/* ================= 视频插入弹窗 ================= */
.we-video-form {
  padding: 4px 0;
}
.we-video-hint {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.7;
  color: #909399;
}
.we-video-hint code {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 5px;
  background: #f2f3f5;
  border-radius: 3px;
  font-family: Consolas, Monaco, monospace;
  font-size: 11px;
  color: #d6336c;
  word-break: break-all;
}
.we-video-form .el-upload-dragger {
  padding: 26px 10px;
}
.we-video-form .el-upload-dragger i {
  font-size: 34px;
  color: #c0c4cc;
  line-height: 1.4;
}

/* 弹框内：编辑器占满宽度 */
.el-dialog .wechat-editor {
  width: 100%;
}
</style>
